import { useId, useState } from 'react';
import { useConfirm } from '@/components/DialogProvider';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  rulesQuery,
  ruleQuery,
  createAutomationRule,
  updateAutomationRule,
  deleteAutomationRule,
  buildRulePatch,
  mergeRuleBody,
  KNOWN_TRIGGERS,
  TRIGGER_LABELS,
  composeTiming,
  decomposeTiming,
  type AutomationRuleResponse,
  type AutomationRuleSummary,
  type KnownTrigger,
  type RuleMaterialBody,
  type TimingFormState,
  type UpdateAutomationRuleRequest,
} from '@/lib/rules';
import { templatesQuery } from '@/lib/templates';
import { ApiError } from '@/lib/api';
import { posthog } from '@/lib/posthog';
import { meQuery } from '@/lib/me';
import { usePageHeader } from '@/components/PageHeader';
import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RuleScheduleFields } from '@/components/automation/RuleScheduleFields';
import {
  RuleConditionFields,
  type ConditionsFormState,
} from '@/components/automation/RuleConditionFields';
import { RuleOverridesSection } from '@/components/automation/RuleOverridesSection';

export const Route = createFileRoute('/_app/rules')({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(meQuery),
      context.queryClient.ensureQueryData(templatesQuery()),
    ]),
  component: RulesPage,
});

function TriggerBadge({ trigger }: { trigger: KnownTrigger }) {
  return <Badge variant="gold">{TRIGGER_LABELS[trigger] ?? trigger}</Badge>;
}

function ActiveBadge({ active }: { active: boolean }) {
  return active ? (
    <Badge variant="success" dot>
      Active
    </Badge>
  ) : (
    <Badge variant="neutral" dot>
      Inactive
    </Badge>
  );
}

interface RuleFormState {
  name: string;
  trigger: KnownTrigger;
  templateId: string;
  timing: TimingFormState;
  conditions: ConditionsFormState;
}

const DEFAULT_FORM: RuleFormState = {
  name: '',
  trigger: 'booking.confirmed',
  templateId: '',
  timing: { direction: 'before', value: 0, unit: 'hours', atLocalTime: '' },
  conditions: { sources: new Set(), hadPriorConversation: undefined, minNights: '' },
};

// ruleToForm builds the form state from the FULL rule response. It must never
// be fed a list-row summary: the summary has no timing/conditions, and a form
// built from it silently wipes both fields on save (that exact bug rewrote the
// production check-in offsets).
function ruleToForm(r: AutomationRuleResponse): RuleFormState {
  const storedTiming = (r.timing ?? {}) as { offset_minutes?: number; at_local_time?: string };
  const storedConds = (r.conditions ?? {}) as {
    source?: string[];
    had_prior_conversation?: boolean;
    min_nights?: number;
  };
  return {
    name: r.name,
    trigger: r.trigger as KnownTrigger,
    templateId: r.template_id,
    timing: decomposeTiming(r.trigger as KnownTrigger, storedTiming),
    conditions: {
      sources: new Set(storedConds.source ?? []),
      hadPriorConversation: storedConds.had_prior_conversation,
      minNights: storedConds.min_nights != null ? String(storedConds.min_nights) : '',
    },
  };
}

// originalBody extracts the material fields of the full rule for diffing.
function originalBody(r: AutomationRuleResponse): RuleMaterialBody {
  return {
    name: r.name,
    trigger: r.trigger as KnownTrigger,
    template_id: r.template_id,
    timing: (r.timing ?? {}) as Record<string, unknown>,
    conditions: (r.conditions ?? {}) as Record<string, unknown>,
  };
}

interface RuleModalProps {
  editing?: AutomationRuleSummary | null;
  templateMap: Map<string, string>;
  onClose: () => void;
  canWrite: boolean;
}

// RuleModal fetches the full rule when editing (the summary from the list has
// no timing/conditions) and only then mounts the form.
export function RuleModal({ editing, templateMap, onClose, canWrite }: RuleModalProps) {
  const fullQ = useQuery({ ...ruleQuery(editing?.id ?? ''), enabled: !!editing });

  if (editing && !fullQ.data) {
    return (
      <dialog
        open
        className="fixed inset-0 z-50 m-auto h-fit w-full max-w-2xl rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-6 shadow-[var(--shadow-lg)]"
        onClose={onClose}
      >
        {fullQ.isError ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-destructive">Failed to load the rule. Close and retry.</p>
            <Button type="button" variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        ) : (
          <p className="text-sm text-muted">Loading rule…</p>
        )}
      </dialog>
    );
  }

  return (
    <RuleModalForm
      original={editing ? (fullQ.data as AutomationRuleResponse) : null}
      templateMap={templateMap}
      onClose={onClose}
      canWrite={canWrite}
    />
  );
}

interface RuleModalFormProps {
  original: AutomationRuleResponse | null; // null = create
  templateMap: Map<string, string>;
  onClose: () => void;
  canWrite: boolean;
}

function RuleModalForm({ original, templateMap, onClose, canWrite }: RuleModalFormProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  // Freeze the baseline at mount: `original` is a live query-bound prop that a
  // background refetch can silently advance. The form was seeded from the
  // mount-time value, so the diff must run against the SAME snapshot — a moving
  // baseline would force-include untouched fields and clobber a concurrent
  // editor's change.
  const [editing] = useState(original);
  const [form, setForm] = useState<RuleFormState>(editing ? ruleToForm(editing) : DEFAULT_FORM);
  const [error, setError] = useState<string | null>(null);
  const askConfirm = useConfirm();

  const nameId = useId();
  const trigId = useId();
  const tmplId = useId();

  function buildBody() {
    return {
      name: form.name,
      trigger: form.trigger,
      template_id: form.templateId,
      timing: composeTiming(form.trigger, form.timing),
      conditions: {
        ...(form.conditions.sources.size > 0
          ? { source: [...form.conditions.sources] }
          : {}),
        ...(form.conditions.hadPriorConversation !== undefined
          ? { had_prior_conversation: form.conditions.hadPriorConversation }
          : {}),
        ...(form.conditions.minNights
          ? { min_nights: parseInt(form.conditions.minNights, 10) }
          : {}),
      },
    };
  }

  const createMut = useMutation({
    mutationFn: () =>
      createAutomationRule({
        throwOnError: true,
        body: buildBody(),
      }),
    onSuccess: () => {
      posthog.capture?.('pms_action', { action: 'rule_created' });
      qc.invalidateQueries({ queryKey: ['rules'] });
      toast({ kind: 'success', title: 'Rule created', message: form.name });
      onClose();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const editingId = editing?.id;

  const updateMut = useMutation({
    mutationFn: (patch: UpdateAutomationRuleRequest) =>
      updateAutomationRule({
        throwOnError: true,
        path: { id: editingId ?? '' },
        body: patch,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rules'] });
      toast({ kind: 'success', title: 'Rule updated', message: form.name });
      onClose();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const deleteMut = useMutation({
    mutationFn: () =>
      deleteAutomationRule({
        throwOnError: true,
        path: { id: editingId ?? '' },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rules'] });
      toast({ kind: 'success', title: 'Rule deleted' });
      onClose();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : String(err)),
  });

  const isPending = createMut.isPending || updateMut.isPending || deleteMut.isPending;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (editing) {
      // PATCH only what actually changed — unchanged material fields stay out
      // of the payload, so an untouched timing/conditions can never be wiped
      // and a name-only edit does not re-materialize the queue. mergeRuleBody
      // first carries over keys the form does not model (unmodeled condition
      // keys, event-trigger timing), so they neither diff nor get dropped.
      const orig = originalBody(editing);
      const patch = buildRulePatch(orig, mergeRuleBody(buildBody(), orig));
      if (Object.keys(patch).length === 0) {
        toast({ kind: 'info', title: 'No changes' });
        onClose();
        return;
      }
      updateMut.mutate(patch);
    } else {
      createMut.mutate();
    }
  }

  const templateOptions = Array.from(templateMap.entries());

  return (
    <dialog
      open
      className="fixed inset-0 z-50 m-auto h-fit max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-0 shadow-[var(--shadow-lg)]"
      onClose={onClose}
    >
      <div className="flex items-center justify-between border-b border-[var(--border-soft)] px-6 py-4">
        <h2 className="text-lg font-semibold text-[var(--text-heading)]">
          {editing ? 'Edit Rule' : 'New Rule'}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded-[var(--radius-control)] p-1 text-muted transition hover:bg-[var(--surface-sunken)]"
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-4 p-6">
        <div className="flex flex-col gap-1">
          <label htmlFor={nameId} className="text-sm font-medium">
            Name
          </label>
          <input
            id={nameId}
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            disabled={!canWrite}
            placeholder="Booking confirmed"
            className="rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)] disabled:opacity-50"
            required
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor={trigId} className="text-sm font-medium">
            Trigger
          </label>
          <select
            id={trigId}
            value={form.trigger}
            onChange={(e) =>
              setForm((f) => ({ ...f, trigger: e.target.value as KnownTrigger }))
            }
            disabled={!canWrite}
            className="rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)] disabled:opacity-50"
          >
            {KNOWN_TRIGGERS.map((t) => (
              <option key={t} value={t}>
                {TRIGGER_LABELS[t]}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor={tmplId} className="text-sm font-medium">
            Template
          </label>
          <select
            id={tmplId}
            value={form.templateId}
            onChange={(e) => setForm((f) => ({ ...f, templateId: e.target.value }))}
            disabled={!canWrite}
            className="rounded-[var(--radius-control)] border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:border-accent focus:ring-[3px] focus:ring-[var(--focus-glow)] disabled:opacity-50"
            required
          >
            <option value="">— select template —</option>
            {templateOptions.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium">When</span>
          <div className="rounded-[var(--radius-control)] border border-[var(--border-soft)] p-3">
            <RuleScheduleFields
              trigger={form.trigger}
              timing={form.timing}
              onChange={(t) => setForm((f) => ({ ...f, timing: t }))}
              disabled={!canWrite}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium">Conditions</span>
          <div className="rounded-[var(--radius-control)] border border-[var(--border-soft)] p-3">
            <RuleConditionFields
              conditions={form.conditions}
              onChange={(c) => setForm((f) => ({ ...f, conditions: c }))}
              disabled={!canWrite}
            />
          </div>
        </div>

        {editing && (
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium">Per-apartment overrides</span>
            <div className="rounded-[var(--radius-control)] border border-[var(--border-soft)] p-3">
              <RuleOverridesSection ruleId={editing.id} disabled={!canWrite} />
            </div>
          </div>
        )}

        {error && (
          <p className="rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="flex items-center justify-between pt-2">
          <div>
            {editing && canWrite && (
              <button
                type="button"
                onClick={async () => {
                  const ok = await askConfirm({
                    title: 'Delete this rule?',
                    destructive: true,
                    confirmLabel: 'Delete',
                  });
                  if (ok) deleteMut.mutate();
                }}
                disabled={isPending}
                className="text-sm text-destructive hover:underline disabled:opacity-50"
              >
                Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            {canWrite && (
              <Button type="submit" variant="primary" loading={isPending} disabled={isPending}>
                {editing ? 'Update' : 'Create'}
              </Button>
            )}
          </div>
        </div>
      </form>
    </dialog>
  );
}

function RulesPage() {
  const me = Route.useLoaderData()[0];
  const canWrite = me.roles.includes('super_admin') || me.roles.includes('automation_editor');

  const { data: rulesData, isLoading, isError } = useQuery(rulesQuery());
  const { data: templatesData } = useQuery(templatesQuery());

  const rules = rulesData?.items ?? [];
  const templates = templatesData?.items ?? [];

  // Build id -> name map once
  const templateMap = new Map<string, string>(templates.map((t) => [t.id, t.name]));

  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<AutomationRuleSummary | null>(null);

  // Title lives in the Topbar (navTitle); publish the live rule count as the
  // subtitle and the "New rule" primary into the Topbar's actions slot.
  usePageHeader({
    subtitle: rulesData
      ? `${rules.length} rule${rules.length === 1 ? '' : 's'}`
      : undefined,
    actions: canWrite ? (
      <Button type="button" variant="primary" size="sm" onClick={() => setShowNew(true)}>
        <Plus className="h-4 w-4" aria-hidden="true" />
        New rule
      </Button>
    ) : undefined,
  });

  return (
    <>
      {isLoading && <p className="text-muted">Loading…</p>}
      {isError && <p className="text-sm text-destructive">Failed to load rules.</p>}

      {!isLoading && !isError && (
        <div className="overflow-hidden overflow-x-auto rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-soft)] bg-[var(--surface-sunken)] text-left text-xs font-semibold uppercase tracking-[0.06em] text-muted">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Trigger</th>
                <th className="px-4 py-3">Template</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted">
                    No rules yet.
                  </td>
                </tr>
              )}
              {rules.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setEditing(r)}
                  className="cursor-pointer border-b border-[var(--border-soft)] transition last:border-0 hover:bg-[var(--surface-sunken)]"
                >
                  <td className="px-4 py-3 font-mono text-xs">{r.code}</td>
                  <td className="px-4 py-3">{r.name}</td>
                  <td className="px-4 py-3">
                    <TriggerBadge trigger={r.trigger as KnownTrigger} />
                  </td>
                  <td className="px-4 py-3 text-muted">
                    {templateMap.get(r.template_id) ?? r.template_id.slice(0, 8) + '…'}
                  </td>
                  <td className="px-4 py-3">
                    <ActiveBadge active={r.is_active} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew && (
        <RuleModal
          templateMap={templateMap}
          onClose={() => setShowNew(false)}
          canWrite={canWrite}
        />
      )}
      {editing && (
        <RuleModal
          editing={editing}
          templateMap={templateMap}
          onClose={() => setEditing(null)}
          canWrite={canWrite}
        />
      )}
    </>
  );
}
